import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Flex,
  HStack,
  Button,
  Text,
  Icon,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaHome, FaChartLine } from 'react-icons/fa';
import NeonDashboardChart from '../components/Charts/NeonDashboardChart';

const Analytics = () => {
  const goldColor = "#FFD700";
  
  // Chart timeframe and visibility state
  const [chartTimeframe, setChartTimeframe] = useState("24h");
  const [chartVisibleSeries, setChartVisibleSeries] = useState({
    jinuPrice: true,
    solPrice: true,
    userProfitLoss: true,
    progressiveJackpot: true,
    activePlayers: true,
    activeBorrowers: true,
    activeStakers: true,
    activeLenders: true
  });

  return (
    <Container maxW="container.xl" py={8}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} color="gray.400" separator="›">
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">
            <Icon as={FaHome} mr={1} />
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink color="blue.300">
            <Icon as={FaChartLine} mr={1} />
            Analytics
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      
      {/* Page Title */}
      <Heading as="h1" size="xl" mb={6} color="white">
        Detailed Analytics Dashboard
      </Heading>
      
      {/* Main Chart Section */}
      <Box 
        p={6} 
        bg="rgba(23, 25, 35, 0.9)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.700"
        boxShadow="0 0 15px rgba(0, 0, 0, 0.3)"
        mb={6}
      >
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md" color="white">Market & Performance Data</Heading>
          <HStack spacing={3} justifyContent="flex-end">
            <Button 
              size="sm" 
              variant={chartTimeframe === "24h" ? "solid" : "outline"}
              colorScheme="gold"
              bg={chartTimeframe === "24h" ? "rgba(255, 215, 0, 0.2)" : "transparent"}
              color={chartTimeframe === "24h" ? goldColor : "gray.400"}
              borderColor={goldColor}
              onClick={() => setChartTimeframe("24h")}
              _hover={{ bg: "rgba(255, 215, 0, 0.1)" }}
            >
              24h
            </Button>
            <Button 
              size="sm" 
              variant={chartTimeframe === "7d" ? "solid" : "outline"}
              colorScheme="gold"
              bg={chartTimeframe === "7d" ? "rgba(255, 215, 0, 0.2)" : "transparent"}
              color={chartTimeframe === "7d" ? goldColor : "gray.400"}
              borderColor={goldColor}
              onClick={() => setChartTimeframe("7d")}
              _hover={{ bg: "rgba(255, 215, 0, 0.1)" }}
            >
              7d
            </Button>
            <Button 
              size="sm" 
              variant={chartTimeframe === "30d" ? "solid" : "outline"}
              colorScheme="gold"
              bg={chartTimeframe === "30d" ? "rgba(255, 215, 0, 0.2)" : "transparent"}
              color={chartTimeframe === "30d" ? goldColor : "gray.400"}
              borderColor={goldColor}
              onClick={() => setChartTimeframe("30d")}
              _hover={{ bg: "rgba(255, 215, 0, 0.1)" }}
            >
              30d
            </Button>
            <Button 
              size="sm" 
              variant={chartTimeframe === "all" ? "solid" : "outline"}
              colorScheme="gold"
              bg={chartTimeframe === "all" ? "rgba(255, 215, 0, 0.2)" : "transparent"}
              color={chartTimeframe === "all" ? goldColor : "gray.400"}
              borderColor={goldColor}
              onClick={() => setChartTimeframe("all")}
              _hover={{ bg: "rgba(255, 215, 0, 0.1)" }}
            >
              All
            </Button>
          </HStack>
        </Flex>
        
        {/* The Chart Component */}
        <NeonDashboardChart chartVisibleSeries={chartVisibleSeries} timeframe={chartTimeframe} />
        
        {/* Chart Controls */}
        <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={3} fontSize="sm" mt={4}>
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.jinuPrice ? "solid" : "outline"}
              colorScheme="blue"
              leftIcon={<Box w="2" h="2" bg="#00E5FF" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                jinuPrice: !prev.jinuPrice
              }))}
            >
              JINU Price
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.solPrice ? "solid" : "outline"}
              colorScheme="green"
              leftIcon={<Box w="2" h="2" bg="#00FF85" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                solPrice: !prev.solPrice
              }))}
            >
              SOL Price
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.userProfitLoss ? "solid" : "outline"}
              colorScheme="pink"
              leftIcon={<Box w="2" h="2" bg="#FF00E5" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                userProfitLoss: !prev.userProfitLoss
              }))}
            >
              Your P/L
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.progressiveJackpot ? "solid" : "outline"}
              colorScheme="yellow"
              leftIcon={<Box w="2" h="2" bg="#E5FF00" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                progressiveJackpot: !prev.progressiveJackpot
              }))}
            >
              Jackpot
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.activePlayers ? "solid" : "outline"}
              colorScheme="purple"
              leftIcon={<Box w="2" h="2" bg="#A78BFA" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                activePlayers: !prev.activePlayers
              }))}
            >
              Players
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.activeBorrowers ? "solid" : "outline"}
              colorScheme="orange"
              leftIcon={<Box w="2" h="2" bg="#FFA500" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                activeBorrowers: !prev.activeBorrowers
              }))}
            >
              Borrowers
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.activeStakers ? "solid" : "outline"}
              colorScheme="cyan"
              leftIcon={<Box w="2" h="2" bg="#00D1FF" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                activeStakers: !prev.activeStakers
              }))}
            >
              Stakers
            </Button>
          </HStack>
          
          <HStack>
            <Button
              size="sm"
              variant={chartVisibleSeries.activeLenders ? "solid" : "outline"}
              colorScheme="red"
              leftIcon={<Box w="2" h="2" bg="#FF5733" borderRadius="full" />}
              onClick={() => setChartVisibleSeries(prev => ({
                ...prev,
                activeLenders: !prev.activeLenders
              }))}
            >
              Lenders
            </Button>
          </HStack>
        </SimpleGrid>
      </Box>
      
      {/* Back to Dashboard Link */}
      <Flex justify="center" mt={6}>
        <Button 
          as={RouterLink} 
          to="/modular-profile" 
          colorScheme="blue" 
          variant="outline"
          leftIcon={<Icon as={FaHome} />}
        >
          Back to Dashboard
        </Button>
      </Flex>
    </Container>
  );
};

export default Analytics;
